/**
 * @fileoverview Maps Portal/Prisma thread list rows (GET threads) into `ChatSession` for client session managers.
 *
 * @purpose Prisma returns nested `ChatThreadConfig`; older code read `thread.config`, which dropped metadata (e.g. persona) on list/hydrate.
 * @usage Called from `MainChatSessionManager`, `ProjectChatSessionManager`, `PortalSessionManager` when normalizing list/create responses.
 * @ownership @korvo/core
 * @dependencies `../types/session-manager` (`ChatSession`)
 * @restrictions Do not invent defaults for missing fields; only normalize known API shapes (`ChatThreadConfig` vs legacy `config`).
 */

import type { ChatSession } from '../types/session-manager'

function pickThreadConfigRecord(thread: Record<string, unknown>): Record<string, unknown> | undefined {
  const rel = thread.ChatThreadConfig
  if (rel !== null && rel !== undefined && typeof rel === 'object') {
    return rel as Record<string, unknown>
  }
  const legacy = thread.config
  if (legacy !== null && legacy !== undefined && typeof legacy === 'object') {
    return legacy as Record<string, unknown>
  }
  return undefined
}

/**
 * Normalizes a thread object returned by Portal list/create APIs into `ChatSession`.
 * Merges `defaultAgentId` from thread config into `metadata.personaId` when metadata has no persona id.
 */
export function mapPortalThreadListRowToChatSession(thread: Record<string, unknown>): ChatSession {
  const cfg = pickThreadConfigRecord(thread)
  const metadataRaw = cfg?.metadata
  const baseMeta: Record<string, unknown> =
    metadataRaw !== null && metadataRaw !== undefined && typeof metadataRaw === 'object' && !Array.isArray(metadataRaw)
      ? { ...(metadataRaw as Record<string, unknown>) }
      : {}

  const defaultAgentId =
    typeof cfg?.defaultAgentId === 'string' && cfg.defaultAgentId.trim() !== '' ? cfg.defaultAgentId.trim() : undefined
  const personaIdExisting =
    typeof baseMeta.personaId === 'string' && baseMeta.personaId.trim() !== '' ? baseMeta.personaId.trim() : undefined
  if (personaIdExisting === undefined && defaultAgentId !== undefined) {
    baseMeta.personaId = defaultAgentId
  }

  const fromColumn = typeof thread.workspaceId === 'string' ? thread.workspaceId.trim() : ''
  const fromMeta = typeof baseMeta.workspaceId === 'string' ? baseMeta.workspaceId.trim() : ''
  const workspaceId = fromColumn !== '' ? fromColumn : fromMeta !== '' ? fromMeta : undefined

  const createdAtRaw = thread.createdAt as string
  const lastActivityRaw = (thread.lastActivityAt as string | undefined) ?? createdAtRaw

  return {
    id: thread.id as string,
    title: (thread.title as string) || `Chat ${new Date(createdAtRaw).toLocaleString()}`,
    threadId: thread.id as string,
    ...(workspaceId !== undefined ? { workspaceId } : {}),
    createdAt: new Date(createdAtRaw).getTime(),
    updatedAt: new Date(lastActivityRaw).getTime(),
    metadata: workspaceId !== undefined ? { ...baseMeta, workspaceId } : baseMeta,
  }
}
